import React, { useEffect, useState } from "react";
import { CheckCircle2, Clock, CircleDot, Inbox } from "lucide-react";

const ICONS = {
  done: { Icon: CheckCircle2, className: "text-white bg-blue-600" },
  waiting: { Icon: Clock, className: "text-white bg-amber-500" },
  closed: { Icon: CircleDot, className: "text-gray-400 bg-gray-100" },
};

const StatusAccordion = ({ recruiter, logs }) => {
  recruiter = recruiter || {};

  // Process logs to create steps with automatic no-reply insertion
  const steps = [];

  if (logs && logs.length > 0) {
    // Create a new array with no-reply states inserted before follow-ups
    const processedLogs = [];

    logs.forEach((log, index) => {
      processedLogs.push(log);

      // If this is a follow_up and the next log is not a no-reply, insert a no-reply
      if (log.status === "follow_up") {
        const nextLog = logs[index + 1];
        if (nextLog && nextLog.status !== "no-reply") {
          processedLogs.push({ status: "no-reply", createdAt: null });
        }
      }
    });

    processedLogs.forEach((log, index) => {
      switch (log.status) {
        case "thread_start":
          steps.push({ title: "Email Sent", kind: "done", date: log.createdAt });
          break;

        case "no-reply": {
          const isLatest = index === processedLogs.length - 1;
          steps.push({
            title: isLatest ? "Awaiting Reply" : "No Reply",
            kind: "waiting",
            date: log.createdAt,
          });
          break;
        }

        case "follow_up": {
          const followUpCount = processedLogs
            .slice(0, index + 1)
            .filter((l) => l.status === "follow_up").length;
          steps.push({
            title: `Follow-up ${followUpCount}`,
            kind: "done",
            date: log.createdAt,
          });
          break;
        }

        case "closed":
          steps.push({ title: "Closed", kind: "closed", date: log.createdAt });
          break;

        default:
          break;
      }
    });

    const hasClosedState = steps.some((step) => step.title === "Closed");
    if (!hasClosedState && steps.length > 0) {
      steps.push({ title: "Closed", kind: "closed", date: null });
    }
  }

  // How many steps from the start count as "reached" — everything except a
  // trailing, not-yet-happened "Closed" placeholder.
  const reachedCount =
    steps[steps.length - 1]?.title === "Closed" ? steps.length - 1 : steps.length;

  // Animate the connecting lines in on mount instead of popping in filled.
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (steps.length === 0) {
    return (
      <div className="py-10 text-center">
        <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No activity on this thread yet.</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-gray-500 mb-8">
        Recruiter:{" "}
        <span className="font-semibold text-gray-900">
          {recruiter?.name || recruiter?.email}
        </span>
      </p>

      <div className="overflow-x-auto pb-2">
        <div className="flex items-start min-w-[480px] px-2">
          {steps.map((step, index) => {
            const { Icon, className } = ICONS[step.kind];
            const isLast = index === steps.length - 1;
            const segmentFilled = animated && index < reachedCount - 1;

            return (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center text-center w-20 shrink-0">
                  <span
                    className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${className}`}
                  >
                    <Icon size={16} />
                  </span>
                  <div className="text-xs font-semibold text-gray-900 mt-2.5">
                    {step.title}
                  </div>
                  {step.date && (
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {new Date(step.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                  )}
                </div>

                {!isLast && (
                  <div className="flex-1 h-0.5 mt-4 bg-gray-200 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-all duration-700 ease-out"
                      style={{ width: segmentFilled ? "100%" : "0%" }}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StatusAccordion;
